import { useState, useEffect, useRef } from 'react';
import api from '../../../lib/api';

export interface SurveyResponse {
  id: number;
  survey_id: number;
  question_number: number | string | null;
  user_response: string | number | boolean | null;
  i_customer?: number | string | null;
  user_id?: number | string | null;
  created_at: string | Date;
  updated_at?: string | Date | null;
  [key: string]: unknown;
}

export interface Survey {
  id: number;
  name: string;
  description?: string;
  brand_id?: string | number | null;
  questions: {
    nodes: any[];
    edges: any[];
  } | null;
}

interface UseSurveyDataResult {
  survey: Survey | null;
  responses: SurveyResponse[];
  loading: boolean;
  error: string | null;
  timeGrouping: 'day' | 'week' | 'month' | 'year';
  setTimeGrouping: (grouping: 'day' | 'week' | 'month' | 'year') => void;
}

export function useSurveyData(surveyId: string | undefined): UseSurveyDataResult {
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeGrouping, setTimeGrouping] = useState<'day' | 'week' | 'month' | 'year'>('day');
  const hasAutoSelectedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!surveyId) return;

    // Validate ID is numeric before making API calls
    const numericId = parseInt(surveyId, 10);
    if (isNaN(numericId) || numericId <= 0) {
      setError('Invalid survey ID');
      setLoading(false);
      return;
    }

    // Reset auto-selection flag when survey ID changes
    if (hasAutoSelectedRef.current !== surveyId) {
      hasAutoSelectedRef.current = null;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [surveyRes, responsesRes] = await Promise.all([
          api.get(`/surveys/${numericId}`),
          api.get(`/surveys/${numericId}/responses`),
        ]);
        setSurvey(surveyRes.data || null);
        // Handle new paginated response format or legacy array format
        const responseData = responsesRes.data;
        const fetchedResponses = Array.isArray(responseData) 
          ? responseData 
          : (responseData?.data || []);
        setResponses(fetchedResponses);
        
        // Auto-select grouping only once when page first loads for this survey
        if (fetchedResponses.length > 0 && hasAutoSelectedRef.current !== surveyId) {
          // Calculate date range from responses
          const dates: Date[] = [];
          fetchedResponses.forEach((response: SurveyResponse) => {
            if (response.created_at) {
              try {
                const date = new Date(response.created_at);
                if (!isNaN(date.getTime())) {
                  dates.push(date);
                }
              } catch (e) {
                // Log invalid dates for debugging
                console.warn('Invalid date in survey response:', response.created_at, e);
              }
            }
          });
          
          if (dates.length > 0) {
            dates.sort((a, b) => a.getTime() - b.getTime());
            const firstTime = dates[0].getTime();
            const lastTime = dates[dates.length - 1].getTime();
            const daysDiff = Math.ceil((lastTime - firstTime) / (1000 * 60 * 60 * 24));
            
            let bestGrouping: 'day' | 'week' | 'month' | 'year' = 'day';
            
            if (daysDiff <= 30) {
              bestGrouping = 'day';
            } else if (daysDiff <= 90) {
              bestGrouping = 'week';
            } else if (daysDiff <= 360*5) { // ~2 years
              bestGrouping = 'month';
            } else {
              bestGrouping = 'year';
            }
            
            setTimeGrouping(bestGrouping);
            hasAutoSelectedRef.current = surveyId; // Mark that we've auto-selected for this survey
          }
        }
      } catch (err: any) {
        console.error('Failed to load survey data:', err);
        setError(err.response?.data?.message || 'Failed to load survey data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [surveyId]);

  return {
    survey,
    responses,
    loading,
    error,
    timeGrouping,
    setTimeGrouping,
  };
}

